import React, { useEffect } from "react";
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  IconButton,
  Button,
  Typography,
  CircularProgress,
  Paper,
  Tooltip,
  Chip,
  Snackbar,
  Alert,
  Slide,
  useTheme,
  useMediaQuery,
  Container,
} from "@mui/material";
import { SwapHoriz, ContentCopy, UploadFile, } from "@mui/icons-material";

import { TRADUCTOR_CONFIG } from "./config/traductor.config";
import { useLanguageDetection } from "./hooks/useLanguageDetection";
import { useTraductorLogic } from "./hooks/useTraductorLogic";

import "./traductor.css";
import icon_traductor_off from "../../../assets/traductor.png";
import HeroHeader from "../../HeroHeader";

const { LANGUAGES, MAX_WORDS, DEFAULT_PAIR } =
  TRADUCTOR_CONFIG;

const sxPage = { px: { xs: 2, sm: 4, md: 8, lg: 12 } };
const LOCALSTORAGE_KEYS_TO_CLEAR = [
  "sac_hash_step3",
  "sac_step2_data",
  "sac_step5_data",
  "sac_temas_generados",
  "seleccionPaso1",
  "seleccionPaso2",
  "seleccionPaso2_key",
  "seleccionPaso3",
  "sesionesPaso3",
  "sesionesPaso3_key",
  "temasPaso3",
  "temasPaso3_key",
  "ultimoMongoId",
];

function SlideUp(props) {
  return <Slide {...props} direction="up" />;
}

const rowsConfig = {
  xs: { min: 4, max: 6 },
  sm: { min: 5, max: 7 },
  xl: { min: 16, max: 18 },
};

const ResponsiveTextField = React.memo((props) => {
  const theme = useTheme();

  const isUp = {
    xl: useMediaQuery(theme.breakpoints.up("xl")),
    sm: useMediaQuery(theme.breakpoints.up("sm")),
  };

  const current =
    isUp.xl ? "xl" :
      isUp.lg ? "lg" :
        isUp.md ? "md" :
          isUp.sm ? "sm" :
            "xs";

  const { min, max } = rowsConfig[current];

  return (
    <TextField
      {...props}
      multiline
      fullWidth
      minRows={min}
      maxRows={max}
    />
  );
});

const Traductor = () => {

  const {
    texto,
    traduccion,
    idiomaOrigen,
    idiomaDestino,
    archivoNombre,
    loading,
    fileLoading,
    snack,
    palabras,
    textoExcedeLimite,
    fileInputRef,

    setIdiomaOrigen,
    setIdiomaDestino,

    handleTextoChange,
    handleTraducir,
    handleFileUpload,
    intercambiarIdiomas,
    limpiarCampos,
    copiarTexto,
    actualizarDestinoAlCambiarOrigen,
    closeSnack,
  } = useTraductorLogic();

  const {
    idiomaSugerido,
    idiomaBloqueadoPorUsuario,
    bloquearDeteccionPorUsuario,
    reactivarDeteccionAutomatica,
  } = useLanguageDetection({
    texto,
    idiomaOrigen,
    disabled: loading,
  });

  useEffect(() => {
    LOCALSTORAGE_KEYS_TO_CLEAR.forEach((key) => localStorage.removeItem(key));
  }, []);

  const handleIdiomaOrigenChange = (nuevo) => {
    setIdiomaOrigen(nuevo);
    bloquearDeteccionPorUsuario();
    actualizarDestinoAlCambiarOrigen(nuevo);
  };

  const handleIdiomaDestinoChange = (nuevo) => {
    setIdiomaDestino(nuevo);
    bloquearDeteccionPorUsuario();
  };

  return (
    <Box sx={{ marginBottom: 2 }}>
      <Box
        sx={{
          py: { xs: 3, md: 4 },
          px: { xs: 2, md: 4 },
        }}>
        <HeroHeader
          img={icon_traductor_off}
          subtitle="HERRAMIENTAS AL DOCENTE"
          title="Traductor"
          description="Traduce textos entre múltiples idiomas con detección automática, carga de archivos y control de límite de palabras."
          maxWidth="xxl"
        />
      </Box>
      <Container maxWidth="xxl" sx={sxPage}>

        <Box className="lang-row">

          <TextField
            select
            label="Idioma origen"
            value={idiomaOrigen}
            onChange={(e) => handleIdiomaOrigenChange(e.target.value)}
            className="lang-select"
            sx={{ mt: .2, mb: .2 }}
          >
            {LANGUAGES.map((i) => (
              <MenuItem key={i.code} value={i.code}>
                {i.label}
              </MenuItem>
            ))}
          </TextField>

          <IconButton
            onClick={intercambiarIdiomas}
            className="swap-btn"
          >
            <SwapHoriz sx={{ color: "#9CA3AF" }} />
          </IconButton>

          <TextField
            select
            label="Idioma destino"
            value={idiomaDestino}
            onChange={(e) => handleIdiomaDestinoChange(e.target.value)}
            className="lang-select"
          >
            {LANGUAGES
              .filter((i) => i.code !== idiomaOrigen)
              .map((i) => (
                <MenuItem key={i.code} value={i.code}>
                  {i.label}
                </MenuItem>
              ))}
          </TextField>
        </Box>

        <Grid container spacing={2} className="panels-grid">

          <Grid item xs={12} lg={6} sx={{ minHeight: 0 }}>
            <Paper
              elevation={0}
              className="panel panel--left"
              sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}
            >
              <Button
                className="upload-btn"
                variant="outlined"
                fullWidth
                component="label"
                startIcon={<UploadFile />}
                disabled={fileLoading}
                sx={{ mb: 1 }}
              > <Box
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  width: "100%",
                  textAlign: "left",
                }}
              >
                  {fileLoading
                    ? "Procesando archivo..."
                    : "Subir un archivo de máximo 4 páginas (2000 palabras aprox.)"}
                </Box>

                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  accept=".pdf,.doc,.docx,.txt"
                  onChange={handleFileUpload}
                />
              </Button>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: .5, flex: "none !important" }}
              >
                Tamaño máximo permitido: 5 MB
              </Typography>

              {archivoNombre && (
                <Chip
                  icon={<UploadFile />}
                  label={archivoNombre}
                  variant="outlined"
                  className="file-chip"
                />
              )}

              <ResponsiveTextField
                className="input-area"
                label="Texto a traducir"
                value={texto}
                onChange={handleTextoChange}
                placeholder="Ingrese el texto a traducir..."
                sx={{ flex: 1, minHeight: 0, mt: 2 }}
              />

              {idiomaSugerido && (
                <Box
                  sx={{
                    mt: 1,
                    px: 2,
                    py: 1,
                    borderRadius: "12px",
                    backgroundColor: "#f3f4f6",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    fontSize: 13,
                    gap: 1,
                  }}
                >
                  <Typography variant="caption">
                    Parece que el texto está en{" "}
                    <strong>
                      {LANGUAGES.find(l => l.code === idiomaSugerido)?.label}
                    </strong>
                  </Typography>

                  <Button
                    size="small"
                    onClick={() => {
                      const nuevoOrigen = idiomaSugerido;

                      setIdiomaOrigen(nuevoOrigen);

                      let nuevoDestino = DEFAULT_PAIR[nuevoOrigen] ?? "es";

                      if (nuevoDestino === nuevoOrigen) {
                        nuevoDestino = "en";
                      }

                      setIdiomaDestino(nuevoDestino);
                      bloquearDeteccionPorUsuario();
                    }}
                  >
                    Cambiar
                  </Button>
                </Box>
              )}

              {idiomaBloqueadoPorUsuario && !idiomaSugerido && (
                <Box sx={{ mt: 0.5 }}>
                  <Button
                    size="small"
                    variant="text"
                    onClick={reactivarDeteccionAutomatica}
                    sx={{ fontSize: 12, textTransform: "none" }}
                  >
                    Volver a detectar idioma automáticamente
                  </Button>
                </Box>
              )}

              <Box className="panel-footer">
                <Typography
                  variant="caption"
                  className={`word-count ${textoExcedeLimite ? "word-count--error" : ""}`}
                >
                  {palabras} / {MAX_WORDS} palabras
                </Typography>

                <Box className="panel-footer-actions">
                  <Button
                    className="clear-btn-pill"
                    variant="outlined"
                    onClick={limpiarCampos}
                  >
                    Limpiar
                  </Button>

                  <Button
                    className="translate-btn-small"
                    variant="contained"
                    onClick={handleTraducir}
                    disabled={loading || !texto.trim() || textoExcedeLimite}
                  >
                    {loading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      "Traducir"
                    )}
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} lg={6} sx={{ minHeight: 0 }}>
            <Paper
              elevation={0}
              className="panel panel--right"
              sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}
            >
              <Box className="panel-topbar">
                <Typography className="panel-title">Traducción</Typography>

                <Tooltip title="Copiar texto traducido">
                  <span>
                    <IconButton
                      className="copy-btn"
                      onClick={copiarTexto}
                      disabled={!traduccion}
                      size="small"
                    >
                      <ContentCopy />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>

              {loading && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  Procesando traducción…
                </Typography>
              )}

              <ResponsiveTextField
                className="input-area"
                value={traduccion}
                placeholder={
                  loading
                    ? "Traduciendo..."
                    : "La traducción aparecerá aquí..."
                }
                InputProps={{ readOnly: true }}
                sx={{ flex: 1, minHeight: 0 }}
              />
            </Paper>
          </Grid>

        </Grid>

        <Snackbar
          open={snack.open}
          autoHideDuration={3500}
          onClose={closeSnack}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          TransitionComponent={SlideUp}
        >
          <Alert severity={snack.severity} variant="filled" onClose={closeSnack}>
            {snack.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default Traductor;
